import { useState } from 'react';

import { ROUTES } from '@elara/client-core/constants';

import { isNotificationSupported, requestNotificationPermission } from '#lib/web-notifications.js';
import { useNotificationsState, useRouterState } from '#states/hooks.js';
import { Breadcrumbs } from '#ui/Breadcrumbs.js';
import { Button, Card, CardContent, CardHeader, CardTitle } from '#ui/index.js';
import { MaterialIcon } from '#ui/MaterialIcon.js';
import { AppLayout, AppLayoutTitle } from '#views/layout/AppLayout.jsx';

const TITLE_CLASSES = 'text-xl font-regular';

export function NotificationsSettingsPage() {
  const routerState = useRouterState();
  const notificationsState = useNotificationsState();

  const supported = isNotificationSupported();
  const [permission, setPermission] = useState<NotificationPermission>(supported ? Notification.permission : 'denied');
  const isGranted = permission === 'granted';
  const isDenied = permission === 'denied';

  const handleEnable = async () => {
    const result = await requestNotificationPermission();
    if (result) setPermission(result);
    if (result === 'granted') {
      await notificationsState.initFCM();
    }
  };

  return (
    <AppLayout
      backButtonRoute={ROUTES.ACCOUNT}
      title={<AppLayoutTitle title="Notifications" />}
      transitionKey={routerState.currentRoute?.path}
    >
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <div className="max-w-2xl mx-auto w-full px-4 pt-12 pb-16 flex flex-col gap-6">
            <Breadcrumbs items={[{ label: 'Account', to: ROUTES.ACCOUNT }, { label: 'Notifications' }]} />

            <div>
              <h1 className="text-2xl font-display font-light">Notifications</h1>
              <p className="text-sm text-muted-foreground mt-1">Manage how you receive notifications from Elara.</p>
            </div>

            <Card className="shadow-sm p-6 dark:bg-background-secondary">
              <CardHeader className="p-0 pb-4 sr-only">
                <CardTitle className={TITLE_CLASSES}>Push Notifications</CardTitle>
              </CardHeader>
              <CardContent className="p-0 space-y-4">
                {!supported ? (
                  <div className="flex items-start gap-3 text-muted-foreground">
                    <MaterialIcon icon="notifications_off" className="mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Not supported</p>
                      <p className="text-sm text-pretty">
                        Your browser doesn’t support push notifications. Try using Chrome, Firefox, or Edge.
                      </p>
                    </div>
                  </div>
                ) : isGranted ? (
                  <div className="flex items-start gap-3">
                    <MaterialIcon icon="notifications_active" className="text-success mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Notifications enabled</p>
                      <p className="text-sm text-muted-foreground text-pretty">
                        You’ll receive push notifications for replies from your concierge, booking updates, trip reminders, and more.
                      </p>
                      <p className="text-sm text-muted-foreground mt-2 text-pretty">
                        To turn off notifications, update the notification permissions for this site in your browser
                        settings.
                      </p>
                    </div>
                  </div>
                ) : isDenied ? (
                  <div className="flex items-start gap-3 text-muted-foreground">
                    <MaterialIcon icon="notifications_off" className="mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Notifications blocked</p>
                      <p className="text-sm text-pretty">
                        You’ve blocked notifications for this site. To enable them, update your browser’s site settings
                        and allow notifications for Elara.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <MaterialIcon icon="notifications" className="text-muted-foreground mt-0.5" />
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium">Enable push notifications</p>
                        <p className="text-sm text-muted-foreground text-pretty">
                          Get notified about replies from your concierge, booking confirmations, trip updates, and more
                          — even when you’re not using Elara.
                        </p>
                      </div>
                      <Button variant="default" size="sm" onClick={() => void handleEnable()}>
                        Enable Notifications
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
